import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import debounce from "lodash.debounce";
import { arrayOf, shape, string, object } from "prop-types";

import { isScrolledIntoView } from "../../common/scrollIntoView.js";

import FundingInterface from "./FundingInterface.jsx";
import FundingGraph from "./FundingGraph.jsx";

const propTypes = {
  dictionary: shape({
    graphTotal: string,
    graphPartial: string,
  }),
  totalLabel: string,
  totalNumber: string,
  items: arrayOf(
    shape({
      label: string,
      description: string,
      amountString: string,
      amountNumber: string,
      link: object,
    }).isRequired,
  ),
};

const defaultProps = {
  items: [],
};

// green-00, green-05, green-10, green-20, green-30, green-40, green-50, green-60, green-70, green-80, green-90, green-100
const chartColors = [
  "#f4fbf7",
  "#d6efe0",
  "#b9e4ce",
  "#9cd9c5",
  "#7ecdb4",
  "#61c2aa",
  "#4fab9c",
  "#428f8e",
  "#357372",
  "#295958",
  "#1b3b3b",
  "#0e1f1f",
];

const convert2Number = (value) => Math.round(parseFloat(value));

const FundingInteractiveApp = (props) => {
  const { items, dictionary, totalLabel } = props;

  const [initialized, setIntialized] = useState(false);
  const [width, setWidth] = useState(740);
  const [activeItemIndex, setActiveItemIndex] = useState(-1);

  const inactiveColor = "#e6e6e6";

  const graphContainer = useRef();

  const handleSelectionClick = useCallback(
    (index) => {
      if (index === activeItemIndex) {
        setActiveItemIndex(-1);
      } else {
        setActiveItemIndex(index);
      }
    },
    [activeItemIndex],
  );

  const colorArray = useMemo(() => {
    const length = items.length;
    let startIndex = 2; // green-10
    let offset = 1; // no gaps

    if (length <= 3 && length > 0) {
      return chartColors.slice(1, length + 1);
    }

    switch (length) {
      case 4:
        offset = 2; // gaps between colors
        startIndex = 3; // green-20
        break;
      case 5:
        offset = 2; // gaps between colors
        break;
      case 6:
      case 7:
        startIndex = 3; // green-20
        break;
      case 11:
        return chartColors.slice(1); // all but first
      case 12:
        return chartColors; // all colors
      default:
    }

    // loop through each to get a color based on startIndex and offset values
    return items.map((item, i) => {
      return chartColors[i * offset + startIndex];
    }, startIndex);
  }, [items]);

  const chartValues = useMemo(() => {
    return items.map((item, i) => {
      if (initialized) {
        return {
          index: i,
          value: convert2Number(item.amountNumber),
          label: String.fromCharCode("A".charCodeAt(0) + i),
          color:
            activeItemIndex === i || activeItemIndex === -1
              ? colorArray[i]
              : inactiveColor,
        };
      } else {
        return {
          index: i,
          value: 10,
          label: "",
          color: inactiveColor,
        };
      }
    });
  }, [activeItemIndex, colorArray, initialized, items]);

  useEffect(() => {
    const resizeDebounce = debounce(() => {
      if (graphContainer.current) {
        setWidth(graphContainer.current.offsetWidth);
      }
    }, 10);

    window.addEventListener("resize", resizeDebounce);

    return () => {
      resizeDebounce.cancel();
      window.removeEventListener("resize", resizeDebounce);
    };
  }, [chartValues, handleSelectionClick, graphContainer]);

  useEffect(() => {
    setWidth(graphContainer.current.offsetWidth);
  }, [graphContainer]);

  useEffect(() => {
    const scrollDebounce = debounce(() => {
      if (isScrolledIntoView(graphContainer.current)) {
        setIntialized(true);
        window.removeEventListener("scroll", scrollDebounce);
      }
    }, 10);

    if (!initialized) {
      window.addEventListener("scroll", scrollDebounce);
    }

    return () => {
      scrollDebounce.cancel();
      window.removeEventListener("scroll", scrollDebounce);
    };
  }, [graphContainer, initialized]);

  return (
    <>
      <div
        className="funding-interactive__graph-container js-funding-interactive-graph"
        ref={graphContainer}
      >
        <div className="funding-interactive__graph-content" aria-live="polite">
          <span className="funding-interactive__graph-label">
            {activeItemIndex >= 0
              ? dictionary.graphPartial
              : dictionary.graphTotal}
          </span>
          <span className="funding-interactive__graph-value">
            {activeItemIndex >= 0
              ? items[activeItemIndex].amountString
              : totalLabel}
          </span>
        </div>
        <FundingGraph
          width={width}
          chartValues={chartValues}
          handleClick={handleSelectionClick}
        />
      </div>
      <div className="funding-interactive__app">
        <FundingInterface
          {...props}
          activeItemIndex={activeItemIndex}
          setActiveItemIndex={handleSelectionClick}
        />
      </div>
    </>
  );
};

FundingInteractiveApp.propTypes = propTypes;
FundingInteractiveApp.defaultProps = defaultProps;
export default FundingInteractiveApp;
